// pages/UserEdit.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Smartphone, Save, Loader2, ArrowLeft } from 'lucide-react';
import { updateUserStatus, getUserById } from '../Masters/AdminAPI';
import { Panel } from '../Masters/UiElements';

const ReadOnlyField = ({ label, value }) => (
    <div className="mb-4">
        <label className="block text-sm font-medium mb-1.5 text-gray-600">{label}</label>
        <div className="w-full px-4 py-2.5 rounded-xl bg-gray-50 border border-gray-200 text-gray-500 text-sm">
            {value || '—'}
        </div>
    </div>
);

const UserEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [status, setStatus] = useState('active');
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const loadUser = async () => {
            try {
                setLoading(true);
                const res = await getUserById(id);
                setUser(res.user);
                setStatus(res.user.status || 'active');
            } catch (err) {
                setError(err?.response?.data?.message || err.message || 'Failed to load user');
            } finally {
                setLoading(false);
            }
        };
        if (id) loadUser();
    }, [id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess(false);

        try {
            const res = await updateUserStatus(id, status);
            if (!res.success) throw new Error(res.message || 'Update failed');
            setSuccess(true);
            setTimeout(() => {
                navigate('/admin/users');
            }, 1500);
        } catch (err) {
            setError(err?.response?.data?.message || err.message || 'Failed to update user status');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <Loader2 size={40} className="animate-spin text-orange-500 mx-auto" />
                    <p className="mt-4 text-gray-600">Loading user details...</p>
                </div>
            </div>
        );
    }

    if (error && !user) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <div className="text-red-500 mb-4">⚠️</div>
                    <p className="text-gray-600">{error}</p>
                    <button 
                        onClick={() => navigate('/admin/users/all')}
                        className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-xl hover:bg-orange-600"
                    >
                        Back to Users
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
            <div className="max-w-4xl mx-auto">
                {/* Header with Back Button */}
                <div className="flex items-center gap-4 mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 rounded-xl hover:bg-orange-100 transition-colors text-gray-600 hover:text-orange-600"
                    >
                        <ArrowLeft size={24} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">Edit User</h1>
                        <p className="text-sm text-gray-500">Update user device information and status</p>
                    </div>
                </div>

                {/* User Edit Form */}
                <div className="bg-white rounded-2xl shadow-xl border border-orange-200 overflow-hidden">
                    <div className="px-6 py-4 border-b border-orange-100 bg-gradient-to-r from-orange-50 to-amber-50">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-md">
                                <Smartphone size={24} className="text-white" />
                            </div>
                            <div>
                                <h2 className="text-lg font-bold text-gray-800">{user?.name}</h2>
                                <p className="text-xs text-gray-500">{user?.email}</p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6">
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <ReadOnlyField label="Name" value={user?.name} />
                                <ReadOnlyField label="Email" value={user?.email} />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <ReadOnlyField label="Phone Number" value={user?.phoneNumber} />
                                <ReadOnlyField label="Device Name" value={user?.deviceName} />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <ReadOnlyField label="Device Type" value={user?.deviceType} />
                                <ReadOnlyField label="Connection Code" value={user?.deviceConnectionCode} />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <ReadOnlyField 
                                    label="Last Login" 
                                    value={user?.lastLogin ? new Date(user.lastLogin).toLocaleString() : '—'} 
                                />
                                <div>
                                    <label className="block text-sm font-medium mb-1.5 text-gray-600">
                                        Status <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={status}
                                        onChange={(e) => setStatus(e.target.value)}
                                        className="w-full px-4 py-2.5 rounded-xl transition-all bg-orange-50 border border-orange-200 text-gray-800 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 focus:outline-none"
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                    <p className="text-xs text-gray-400 mt-1">Changing status will affect user's access</p>
                                </div>
                            </div>

                            {error && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                                    <p className="text-sm text-red-600 flex items-center gap-2">
                                        <span className="text-red-500">⚠</span> {error}
                                    </p>
                                </div>
                            )}

                            {success && (
                                <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                                    <p className="text-sm text-green-600 flex items-center gap-2">
                                        <span className="text-green-500">✓</span> User status updated successfully! Redirecting...
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-orange-100">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/users')}
                                disabled={saving}
                                className="px-4 py-2.5 text-sm font-medium rounded-xl transition-colors text-gray-600 hover:bg-gray-50 border border-gray-200 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-xl transition-all bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-lg hover:shadow-orange-500/30 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default UserEdit;